/**
 * The clock that runs an LVC simulation.
 *
 * It replaces a 100 ms setInterval that called setState on every tick, which re-rendered the
 * whole scenario page ten times a second. Here positions are computed on each animation
 * frame and handed to subscribers directly; React is only involved through a subscriber that
 * is deliberately throttled, so the map moves at frame rate while the panels update a few
 * times a second.
 *
 * Elapsed time is always measured against the wall clock, never accumulated per tick, so a
 * throttled background tab renders less often without the simulation drifting.
 *
 * Plan: plans/lvc-revamp/eng-plan.md.
 */

import { updateAgentStates } from './simulationEngine';
import { sampleAt, behaviorKey } from './datasetCache';

/** How often the React-facing subscriber is allowed to fire. */
export const UI_UPDATE_MS = 250;

/** Step size used while the tab is hidden and animation frames are not delivered. */
export const HIDDEN_TICK_MS = 200;

/**
 * Animation frames stop entirely in a hidden tab, which would freeze a running simulation
 * while the once-a-second upload kept sending its last position. So scheduling falls back to
 * a timer whenever the page is hidden, and switches back to frames when it is shown.
 */
export const defaultScheduler = {
  isHidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
  request(cb) {
    return this.isHidden()
      ? { timeout: window.setTimeout(cb, HIDDEN_TICK_MS) }
      : { frame: window.requestAnimationFrame(cb) };
  },
  cancel(handle) {
    if (!handle) return;
    if (handle.timeout !== void 0) window.clearTimeout(handle.timeout);
    else window.cancelAnimationFrame(handle.frame);
  },
};

/**
 * Position for one agent at `simulationTime`, interpolated between dataset samples.
 *
 * @param {Object} agentState - from simulationEngine
 * @param {number} simulationTime - seconds since the run started
 * @param {Map} indexes - preloadBehaviors output
 * @param {Map} cursors - uid -> last index used, carried between frames (mutated)
 * @returns {{position: {lat:number, lon:number}|null, sample: Object|null, exact: Object|null}}
 */
export const positionAt = (agentState, simulationTime, indexes, cursors) => {
  const behaviors = agentState.agent.behaviors || [];
  const behavior = behaviors[agentState.currentBehaviorIndex];
  if (!behavior) return { position: agentState.currentPosition, sample: null, exact: null };

  if (behavior.type === 'wait') {
    const hasLocation = behavior.latitude !== void 0 && behavior.longitude !== void 0;
    return {
      position: hasLocation ? { lat: behavior.latitude, lon: behavior.longitude } : null,
      sample: null,
      exact: null,
    };
  }

  if (behavior.type !== 'datasetview') return { position: agentState.currentPosition, sample: null, exact: null };

  const uid = agentState.agent.uid;
  const index = indexes.get(behaviorKey(uid, agentState.currentBehaviorIndex));
  if (!index || index.points.length === 0) return { position: agentState.currentPosition, sample: null, exact: null };

  const target = behavior.start_time + (simulationTime - agentState.behaviorStartTime);
  const cursorKey = behaviorKey(uid, agentState.currentBehaviorIndex);
  const { sample, exact, cursor, position } = sampleAt(index, target, cursors.get(cursorKey) ?? 0);
  cursors.set(cursorKey, cursor);
  return { position, sample, exact };
};

/**
 * Advance every agent to `simulationTime`.
 * @returns {Array} agent states with `currentPosition`, `currentSample` and `hasFirstLocation`
 */
export const stepAgents = (agentStates, simulationTime, indexes, cursors) =>
  updateAgentStates(agentStates, simulationTime).map((agentState) => {
    const { position, sample, exact } = positionAt(agentState, simulationTime, indexes, cursors);
    return {
      ...agentState,
      currentPosition: position ?? agentState.currentPosition,
      currentSample: sample,
      currentExactSample: exact,
      hasFirstLocation: agentState.hasFirstLocation || Boolean(position),
    };
  });

/**
 * Create a clock. Nothing runs until `start`.
 *
 * @param {Object} options
 * @param {Map} options.indexes - preloadBehaviors output
 * @param {(states: Array, elapsed: number) => void} options.onFrame - every animation frame
 * @param {(states: Array, elapsed: number) => void} [options.onUiUpdate] - throttled to UI_UPDATE_MS
 * @param {number} [options.uiUpdateMs]
 * @param {() => number} [options.now] - injectable for tests
 * @param {(cb: Function) => number} [options.requestFrame] - injectable for tests
 * @param {(handle: number) => void} [options.cancelFrame]
 */
export const createSimulationClock = ({
  indexes,
  onFrame,
  onUiUpdate,
  uiUpdateMs = UI_UPDATE_MS,
  now = () => Date.now(),
  requestFrame = (cb) => defaultScheduler.request(cb),
  cancelFrame = (handle) => defaultScheduler.cancel(handle),
  onVisibilityChange = (listener) => {
    if (typeof document === 'undefined') return () => {};
    document.addEventListener('visibilitychange', listener);
    return () => document.removeEventListener('visibilitychange', listener);
  },
}) => {
  const cursors = new Map();
  let states = [];
  let startedAt = null;
  let pausedTotal = 0;
  let pausedAt = null;
  let frameHandle = null;
  let lastUiAt = 0;
  let running = false;
  let stopWatchingVisibility = null;

  // Subtracts the pause that is still open, otherwise elapsed time keeps growing while paused
  // for anything that reads it (the timeline, a seek, the telemetry loop).
  const elapsedSeconds = () => {
    if (startedAt === null) return 0;
    const openPause = pausedAt === null ? 0 : now() - pausedAt;
    return (now() - startedAt - pausedTotal - openPause) / 1000;
  };

  const frame = () => {
    frameHandle = null;
    if (!running) return;

    const elapsed = elapsedSeconds();
    states = stepAgents(states, elapsed, indexes, cursors);
    onFrame(states, elapsed);

    const at = now();
    if (onUiUpdate && at - lastUiAt >= uiUpdateMs) {
      lastUiAt = at;
      onUiUpdate(states, elapsed);
    }
    frameHandle = requestFrame(frame);
  };

  // Switching to or from a hidden tab changes which scheduler applies, and a frame already
  // requested before the tab was hidden will never fire, so reschedule on the switch.
  const reschedule = () => {
    if (!running) return;
    if (frameHandle !== null) cancelFrame(frameHandle);
    frameHandle = requestFrame(frame);
  };

  return {
    start(initialStates) {
      states = initialStates;
      startedAt = now();
      pausedTotal = 0;
      pausedAt = null;
      lastUiAt = 0;
      cursors.clear();
      running = true;
      frameHandle = requestFrame(frame);
      if (!stopWatchingVisibility) stopWatchingVisibility = onVisibilityChange(reschedule);
      return startedAt;
    },
    /**
     * Jump to `seconds` (timeline scrubbing). Rebases the wall-clock origin so the run
     * continues from there, and recomputes positions immediately.
     */
    seek(seconds) {
      const at = now();
      startedAt = at - seconds * 1000;
      pausedTotal = 0;
      pausedAt = running ? null : at;
      cursors.clear();
      states = stepAgents(states, seconds, indexes, cursors);
      onFrame(states, seconds);
      if (onUiUpdate) {
        lastUiAt = at;
        onUiUpdate(states, seconds);
      }
      return states;
    },
    pause() {
      if (!running || pausedAt !== null) return;
      pausedAt = now();
      running = false;
      if (frameHandle !== null) {
        cancelFrame(frameHandle);
        frameHandle = null;
      }
    },
    resume() {
      if (running || pausedAt === null) return;
      pausedTotal += now() - pausedAt;
      pausedAt = null;
      running = true;
      frameHandle = requestFrame(frame);
    },
    stop() {
      running = false;
      startedAt = null;
      pausedTotal = 0;
      pausedAt = null;
      states = [];
      cursors.clear();
      if (frameHandle !== null) {
        cancelFrame(frameHandle);
        frameHandle = null;
      }
      if (stopWatchingVisibility) {
        stopWatchingVisibility();
        stopWatchingVisibility = null;
      }
    },
    getStates: () => states,
    getElapsed: elapsedSeconds,
    isRunning: () => running,
  };
};
